/**
 * TSCORP-410: LV2 Step-Up Authentication Modal — HR Service
 *
 * Displayed when any API call returns HTTP 402 with { error: "stepup_required" }.
 * Redirects to auth.tscorp.ai/step-up with the current URL as returnUrl.
 *
 * GCP Material Light design system.
 */
import React from 'react';
import { AUTH_URL } from '../lib/service-registry';

interface StepUpModalProps {
  /** Whether the modal is visible */
  isOpen: boolean;
  /** Called when the user cancels */
  onClose: () => void;
}

const StepUpModal: React.FC<StepUpModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const handleAuthenticate = () => {
    const returnUrl = encodeURIComponent(window.location.href);
    window.location.href = `${AUTH_URL}/step-up?returnUrl=${returnUrl}`;
  };

  return (
    // Backdrop
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onClose}
      aria-modal="true"
      role="dialog"
      aria-labelledby="stepup-modal-title"
    >
      {/* Modal card */}
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          border: '1px solid #e8eaed',
          boxShadow: '0 8px 24px rgba(60,64,67,0.3)',
          maxWidth: '420px',
          width: '90%',
          padding: '0',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Warning header strip */}
        <div
          style={{
            backgroundColor: '#fef7e0',
            borderBottom: '1px solid #e37400',
            padding: '16px 24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          {/* Warning icon */}
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
            style={{ flexShrink: 0 }}
          >
            <path
              d="M1 21h22L12 2 1 21z"
              fill="#e37400"
              fillOpacity="0.15"
              stroke="#e37400"
              strokeWidth="1.5"
              strokeLinejoin="round"
            />
            <path
              d="M12 9v4"
              stroke="#e37400"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <circle cx="12" cy="16.5" r="1" fill="#e37400" />
          </svg>
          <span
            id="stepup-modal-title"
            style={{
              fontSize: '16px',
              fontWeight: 600,
              color: '#202124',
              lineHeight: 1.4,
            }}
          >
            보안 인증 필요
          </span>
        </div>

        {/* Body */}
        <div style={{ padding: '24px' }}>
          <p
            style={{
              fontSize: '14px',
              color: '#5f6368',
              lineHeight: 1.6,
              margin: '0 0 24px 0',
            }}
          >
            민감한 정보에 접근하려면 추가 인증이 필요합니다.
            <br />
            인증을 완료하면 이 페이지로 자동으로 돌아옵니다.
          </p>

          {/* Actions */}
          <div
            style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '8px 20px',
                borderRadius: '8px',
                border: '1px solid #e8eaed',
                backgroundColor: '#ffffff',
                color: '#5f6368',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                lineHeight: 1.4,
              }}
            >
              취소
            </button>
            <button
              type="button"
              onClick={handleAuthenticate}
              style={{
                padding: '8px 20px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: '#1a73e8',
                color: '#ffffff',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                lineHeight: 1.4,
              }}
            >
              인증하기
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepUpModal;
